/**
 * 实时服务
 * 用于MQTT连接和实时数据更新，支持双向绑定
 * 已迁移到MQTT架构
 */

import { mqttService, MqttServiceCallbacks } from './mqttService';
import { settingsService } from './settingsService';
import { AudioManager } from './audio/AudioManager';
import { authService } from './authService';
import type { MqttAudioDownstreamKind } from '../constants/Codec2Constants';

export interface RealtimeMessage {
  type: string;
  data: any;
  timestamp: number;
}

export interface DeviceStatusUpdate {
  deviceId: string;
  status: 'online' | 'offline';
  lastUpdateTime: number;
}

export interface AudioDataUpdate {
  deviceId: string;
  audioLevel: number;
  audioSpectrum: number[];
  timestamp: number;
}

export interface PerformanceUpdate {
  networkQuality: {
    latency: number;
    jitter: number;
    packetLoss: number;
    bandwidth: number;
  };
  bufferPerformance: {
    bufferSize: number;
    bufferUsage: number;
    underruns: number;
    overruns: number;
    averageLatency: number;
  };
}

// 设备连接信息接口
export interface DeviceConnectionInfo {
  deviceId: number;  // 改为number类型，与后端保持一致
  deviceIp: string;
  tcpPort: number;
  tcpConnectTime: number;
  lastAudioTime: number;
  sessionId: string;
  isOnline: boolean;
  latency: number;
  audioQuality: 'excellent' | 'good' | 'fair' | 'poor';
  networkQuality: 'excellent' | 'good' | 'fair' | 'poor';
  bufferHealth: 'healthy' | 'warning' | 'critical';
  errorCount: number;
  lastErrorTime: number;
  lastErrorType: string;
  connectionUptime: number;
  totalBytesReceived: number;
  totalBytesSent: number;
  averageLatency: number;
  jitter: number;
  packetLoss: number;
  bandwidth: number;
  signalStrength: number;
  noiseLevel: number;
  audioLevel: number;
  spectrumData: number[];
  frequency: number;
  mode: string;
  power: number;
  swr: number;
  temperature: number;
  voltage: number;
  current: number;
  status: 'idle' | 'transmitting' | 'receiving' | 'error';
  lastStatusChange: number;
  statusHistory: Array<{
    status: string;
    timestamp: number;
    duration: number;
  }>;
  performanceMetrics: {
    cpuUsage: number;
    memoryUsage: number;
    diskUsage: number;
    networkUsage: number;
    audioLatency: number;
    processingLatency: number;
    bufferLatency: number;
    totalLatency: number;
  };
  configuration: {
    audioFormat: string;
    sampleRate: number;
    bitDepth: number;
    channels: number;
    bufferSize: number;
    compression: string;
    encryption: boolean;
    authentication: boolean;
    protocol: string;
    version: string;
  };
  capabilities: {
    audioFormats: string[];
    maxSampleRate: number;
    maxBitDepth: number;
    maxChannels: number;
    maxBufferSize: number;
    supportedCompressions: string[];
    supportedEncryptions: string[];
    supportedProtocols: string[];
    maxConnections: number;
    maxBandwidth: number;
  };
  metadata: {
    manufacturer: string;
    model: string;
    serialNumber: string;
    firmwareVersion: string;
    hardwareVersion: string;
    buildDate: string;
    lastUpdate: string;
    uptime: number;
    totalUptime: number;
    restartCount: number;
    errorCount: number;
    warningCount: number;
    infoCount: number;
    debugCount: number;
  };
}

// 设备状态变化通知接口
export interface DeviceStatusChangeNotification {
  type: 'device_status_change';
  changeType: string;
  device: DeviceConnectionInfo;
  timestamp: number;
}

// 设备列表更新通知接口
export interface DeviceListUpdateNotification {
  type: 'device_list_update';
  devices: DeviceConnectionInfo[];
  timestamp: number;
}

// 设备统计信息更新通知接口
export interface DeviceStatisticsUpdateNotification {
  type: 'device_statistics_update';
  statistics: {
    totalDevices: number;
    onlineDevices: number;
  };
  timestamp: number;
}

/**
 * 实时服务类
 * 使用MQTT替代WebSocket
 */
class RealtimeService {
  private messageHandlers: Map<string, ((data: any) => void)[]> = new Map();
  private isInitialized = false;
  private deviceStatusMqttUnsub: (() => void) | null = null;
  
  /**
   * 获取网络配置
   */
  private getNetworkConfig() {
    return settingsService.getNetworkSettings();
  }

  /**
   * 登出或即将销毁 MQTT 时调用：取消设备状态监听，并将 isInitialized 置为 false，
   * 以便下次 connect() 会再次执行 initialize()，向新的 mqttService 注册监听。
   * （否则 mqttService.dispose 清空 listeners 后，本服务仍认为已初始化，换账号后永远不再注册。）
   */
  resetMqttClientSession(): void {
    try {
      this.deviceStatusMqttUnsub?.();
    } catch {
      /* ignore */
    }
    this.deviceStatusMqttUnsub = null;
    this.isInitialized = false;
  }

  /**
   * 初始化服务
   */
  async initialize(): Promise<void> {
    // 检查用户是否已登录，未登录则不初始化
    if (!authService.isAuthenticated()) {
      console.log('RealtimeService: 用户未登录，跳过服务初始化');
      return;
    }

    // 检查是否已经登出（通过 sessionStorage 标志）
    const hasLoggedOut = sessionStorage.getItem('wavelink_logged_out');
    if (hasLoggedOut === 'true') {
      console.log('RealtimeService: 用户已登出，跳过服务初始化');
      return;
    }

    if (this.isInitialized) {
      console.log('RealtimeService: 服务已初始化');
      return;
    }

    try {
      // 设置MQTT事件回调
      const callbacks: MqttServiceCallbacks = {
        onConnect: () => {
          console.log('RealtimeService: MQTT连接成功');
          this.emit('connected', {});
        },
        onDisconnect: (reason) => {
          console.log('RealtimeService: MQTT连接断开:', reason);
          this.emit('disconnected', { reason });
        },
        onAudioReceived: async (deviceId: string, audioData: ArrayBuffer, downstreamKind?: MqttAudioDownstreamKind) => {
          // 播放接收到的音频数据
          try {
            const audioManager = AudioManager.getInstance();
            await audioManager.playReceivedAudio(deviceId, audioData, downstreamKind);
          } catch (error) {
            console.error('RealtimeService: 播放音频失败:', error);
          }
          
          this.emit('audio_data', {
            deviceId,
            audioData,
            timestamp: Date.now()
          });
        },
        onSpectrumReceived: (deviceId: string, spectrumData: Uint8Array | number[]) => {
          const spectrumDataOut =
            spectrumData instanceof Uint8Array ? Array.from(spectrumData) : spectrumData;
          this.emit('spectrum_data', {
            deviceId,
            spectrumData: spectrumDataOut,
            timestamp: Date.now()
          });
        },
        onError: (error: Error) => {
          console.error('RealtimeService: MQTT错误:', error);
          this.emit('error', { error });
        }
      };

      this.deviceStatusMqttUnsub?.();
      this.deviceStatusMqttUnsub = mqttService.addDeviceStatusListener((deviceId, status) => {
        this.emit('device_status_update', {
          deviceId,
          status,
          timestamp: Date.now()
        });
      });

      mqttService.setCallbacks(callbacks);
      this.isInitialized = true;
      console.log('RealtimeService: 服务初始化完成');
      } catch (error) {
      console.error('RealtimeService: 初始化失败:', error);
      throw error;
    }
  }

  /**
   * 连接到MQTT服务
   */
  async connect(): Promise<void> {
    // 检查用户是否已登录，未登录则不连接
    if (!authService.isAuthenticated()) {
      console.log('RealtimeService: 用户未登录，跳过MQTT连接');
      return;
    }

    // 检查是否已经登出（通过 sessionStorage 标志）
    const hasLoggedOut = sessionStorage.getItem('wavelink_logged_out');
    if (hasLoggedOut === 'true') {
      console.log('RealtimeService: 用户已登出，跳过MQTT连接');
      return;
    }

    if (!this.isInitialized) {
      await this.initialize();
    }

    await mqttService.connect();
  }

  /**
   * 断开连接
   */
  async disconnect(): Promise<void> {
    await mqttService.disconnect();
  }

  /**
   * 订阅设备音频
   */
  async subscribeToDeviceAudio(deviceId: string): Promise<void> {
    await mqttService.subscribeToDeviceAudio(deviceId);
  }

  /**
   * 取消订阅设备音频
   */
  async unsubscribeFromDeviceAudio(deviceId: string): Promise<void> {
    await mqttService.unsubscribeFromDeviceAudio(deviceId);
  }

  /**
   * 发送音频数据到设备
   */
  async sendAudioToDevice(deviceId: string, audioData: ArrayBuffer): Promise<void> {
    await mqttService.sendAudioToDevice(deviceId, audioData);
  }

  /**
   * 发送PTT控制命令
   */
  async sendPttCommand(deviceId: string, action: 'start' | 'stop'): Promise<void> {
    await mqttService.sendPttCommand(deviceId, action);
  }

  /**
   * 发送频率设置命令
   */
  async sendFrequencyCommand(deviceId: string, vfoAFrequency: string, vfoBFrequency: string): Promise<void> {
    await mqttService.sendFrequencyCommand(deviceId, vfoAFrequency, vfoBFrequency);
  }

  /**
   * 发送模式设置命令
   */
  async sendModeCommand(deviceId: string, vfoAMode: string, vfoBMode: string): Promise<void> {
    await mqttService.sendModeCommand(deviceId, vfoAMode, vfoBMode);
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    return mqttService.isConnected();
  }

  /**
   * 检查是否已订阅设备
   */
  isSubscribedToDevice(deviceId: string): boolean {
    return mqttService.isSubscribedToDevice(deviceId);
  }

  /**
   * 获取已订阅的设备列表
   */
  getSubscribedDevices(): string[] {
    return mqttService.getSubscribedDevices();
  }

  /**
   * 获取服务状态
   */
  getStatus() {
    return mqttService.getStatus();
  }

  /**
   * 获取连接状态
   */
  getConnectionState(): string {
    const status = mqttService.getStatus();
    if (status.isConnected) {
      return 'connected';
    } else if (status.isConnecting) {
      return 'connecting';
    } else {
      return 'disconnected';
    }
  }

  /**
   * 订阅设备状态主题 device/{userId}/{deviceId}/status
   */
  async requestDeviceStatus(deviceId?: string): Promise<void> {
    try {
      if (deviceId) {
        await mqttService.subscribeDeviceStatus(deviceId);
        console.log('RealtimeService: 已订阅设备状态主题 - 设备ID:', deviceId);
      } else {
        // 请求所有设备状态
        console.log('RealtimeService: 请求所有设备状态');
        // TODO: 实现请求所有设备状态的逻辑
      }
    } catch (error) {
      console.error('RealtimeService: 请求设备状态失败:', error);
      throw error;
    }
  }

  /**
   * 请求音频数据
   */
  async requestAudioData(deviceId: string): Promise<void> {
    // 通过MQTT请求音频数据
    console.log('RealtimeService: 请求音频数据', deviceId);
    // TODO: 实现具体的音频数据请求逻辑
  }

  /**
   * 请求性能数据
   */
  async requestPerformanceData(): Promise<void> {
    // 通过MQTT请求性能数据
    console.log('RealtimeService: 请求性能数据');
    // TODO: 实现具体的性能数据请求逻辑
  }

  /**
   * 订阅设备状态更新
   */
  async subscribeToDeviceStatus(deviceId: string, callback: (status: DeviceStatusUpdate) => void): Promise<() => void> {
    // 订阅设备状态主题
    const handler = (data: any) => {
      if (!deviceId || data.deviceId === deviceId) {
        callback({
          deviceId: data.deviceId,
          status: data.status,
          lastUpdateTime: data.timestamp
        });
      }
    };
    
    this.on('device_status_update', handler);
    
    // 返回取消订阅函数
    return () => {
      this.off('device_status_update', handler);
    };
  }

  /**
   * 订阅音频数据
   */
  async subscribeToAudioData(deviceId: string, callback: (audioData: AudioDataUpdate) => void): Promise<() => void> {
    // 订阅音频数据主题
    const handler = (data: any) => {
      if (!deviceId || data.deviceId === deviceId) {
        callback({
          deviceId: data.deviceId,
          audioLevel: 0, // TODO: 从音频数据中提取
          audioSpectrum: [], // TODO: 从频谱数据中提取
          timestamp: data.timestamp
        });
      }
    };
    
    this.on('audio_data', handler);
    
    // 返回取消订阅函数
    return () => {
      this.off('audio_data', handler);
    };
  }

  /**
   * 订阅性能数据
   */
  async subscribeToPerformance(callback: (performance: PerformanceUpdate) => void): Promise<() => void> {
    // 订阅性能数据主题
    const handler = (data: any) => {
      callback(data);
    };
    
    this.on('performance_data', handler);
    
    // 返回取消订阅函数
    return () => {
      this.off('performance_data', handler);
    };
  }

  /**
   * 添加消息处理器
   */
  on(event: string, handler: (data: any) => void): void {
    if (!this.messageHandlers.has(event)) {
      this.messageHandlers.set(event, []);
    }
    this.messageHandlers.get(event)!.push(handler);
  }

  /**
   * 移除消息处理器
   */
  off(event: string, handler: (data: any) => void): void {
    const handlers = this.messageHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * 触发事件
   */
  private emit(event: string, data: any): void {
    const handlers = this.messageHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`RealtimeService: 事件处理器错误 [${event}]:`, error);
        }
      });
    }
  }

  /**
   * 销毁服务
   */
  async dispose(): Promise<void> {
    console.log('RealtimeService: 销毁服务');
    
    try {
      this.deviceStatusMqttUnsub?.();
      this.deviceStatusMqttUnsub = null;
      await mqttService.dispose();
      this.messageHandlers.clear();
      this.isInitialized = false;
      console.log('RealtimeService: 服务已销毁');
    } catch (error) {
      console.error('RealtimeService: 销毁失败:', error);
      throw error;
    }
  }
}

// 创建单例实例
const realtimeService = new RealtimeService();

// 导出服务实例
export { realtimeService };

// 导出类型（已在上面定义时导出）